import type { BaseEntity, FieldValue, EntityWithRelations } from "../../types";
import { getScheduledFollowupWeek } from "@/lib/utils/task/scheduled-followup-tagging";
import { isoWeekToMondayUtc } from "@/lib/utils/task/iso-week";
import {
  resolveAssignedTo,
  resolveAssignedUsers,
  resolveCompanyId,
  resolveContactId,
  resolveCustomerId,
  resolveTicketId,
  resolveUserReferenceField,
} from "@/lib/crm/table-field-display-resolution";
import { contractStatusLabel } from "@/lib/constants/contract-statuses";

/**
 * Furthest-along status among the contracts linked to a deal, for the derived
 * "Contract" list column. Reads the nested `contractsCollection` selected by
 * GetDeals; returns a display label ("Signed" / "Sent" / …) or "None" when the
 * deal has no contract. Rank favors the most-advanced state so a rep sees the
 * best signal at a glance (a signed contract wins over an also-present draft).
 */
const DEAL_CONTRACT_STATUS_RANK: Record<string, number> = {
  signed: 6,
  partially_signed: 5,
  viewed: 4,
  sent: 3,
  draft: 2,
  declined: 1,
  withdrawn: 1,
  error: 0,
};
function resolveDealContractStatus(item: BaseEntity): string {
  const coll = (
    item as {
      contractsCollection?: { edges?: Array<{ node?: { status?: string | null } | null } | null> } | null;
    }
  ).contractsCollection;
  const edges = coll?.edges ?? [];
  let best: string | null = null;
  let bestRank = -1;
  for (const edge of edges) {
    const status = edge?.node?.status;
    if (!status) continue;
    const rank = DEAL_CONTRACT_STATUS_RANK[status] ?? 0;
    if (rank > bestRank) {
      bestRank = rank;
      best = status;
    }
  }
  if (!best) return "None";
  // Labels come from the shared signing vocabulary (`@wtc/contract-signing`),
  // so this cell says the same word as the contract badge and the phone.
  return contractStatusLabel(best);
}

/**
 * Derive the "Next Follow-Up" display string for a deal from its tags
 * (`followup-on-{YYYY-W##}`). Returns e.g. "Wk 30 · Jul 20" or undefined.
 */
function resolveNextFollowUp(item: BaseEntity): string | undefined {
  const tags = (item as { tags?: string[] | null }).tags ?? undefined;
  const week = getScheduledFollowupWeek(tags);
  if (!week) return undefined;
  const wkNum = week.slice(week.indexOf("W") + 1);
  const monday = isoWeekToMondayUtc(week);
  const dateLabel = monday
    ? monday.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" })
    : "";
  return dateLabel ? `Wk ${Number(wkNum)} · ${dateLabel}` : `Wk ${Number(wkNum)}`;
}

/**
 * Field IDs that render as badges with color classes
 */
export const BADGE_FIELD_IDS = [
  "status",
  "priority",
  "entry_type",
  "is_approved",
  "sync_status",
] as const;

/**
 * Field IDs that render as title/name fields with view actions
 */
export const TITLE_FIELD_IDS = [
  "title",
  "name",
  "ticket_number",
  "ticket_id",
  "customer_id",
] as const;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Get the display value for a field, resolving relation objects when present.
 *
 * Accepts `EntityWithRelations<T>` so all relation keys are typed — no casts needed.
 * Callers that pass a plain `T extends BaseEntity` can widen with a single cast at
 * the call site: `getFieldValue(item as EntityWithRelations<typeof item>, fieldId)`.
 */
export function getFieldValue<TData extends BaseEntity>(
  item: EntityWithRelations<TData>,
  fieldId: string
): FieldValue | Record<string, unknown> | undefined {
  switch (fieldId) {
    case "assigned_users":
      return resolveAssignedUsers(item);

    case "assigned_to":
      return resolveAssignedTo(item) ?? resolveUserReferenceField(item, "user_id");

    case "updated_by":
      return resolveUserReferenceField(item, "updated_by");

    case "contact_id":
      return resolveContactId(item);

    case "customer_id":
      return resolveCustomerId(item);

    case "company_id":
      return resolveCompanyId(item);

    case "ticket_id":
      return resolveTicketId(item);

    case "user_id":
      return resolveUserReferenceField(item, "user_id");

    case "created_by":
      return resolveUserReferenceField(item, "created_by");

    case "next_follow_up":
      return resolveNextFollowUp(item);

    case "contract_status":
      return resolveDealContractStatus(item);

    default:
      // Dynamic field access — BaseEntity index signature covers this
      return item[fieldId];
  }
}
